import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useResource } from '../../hooks/useResource';
import { api, directoryApi, type DirectoryAgent } from '../../lib/api';
import type { Inbox, Team } from '../../lib/types';
import { sx } from './styles';

type Draft = { id?: string; name: string; agentUids: string[]; inboxIds: string[] };

/**
 * Teams — name a team, then add/remove the support agents on it and the inboxes
 * it covers. The generic settings table could only show member COUNTS with no
 * way to actually pick agents; this panel maps every support agent with a
 * checkbox so you can add or remove them.
 */
export function TeamsPanel({ canEdit }: { canEdit: boolean }) {
  const { claims } = useAuth();
  const tenantCode = claims?.tenantCode ?? '';
  const { items: teams, loading } = useResource<Team>('teams');
  const { items: inboxes } = useResource<Inbox>('inboxes');
  const [agents, setAgents] = useState<DirectoryAgent[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantCode) return;
    directoryApi.listAgentsDirectory(tenantCode).then((r) => setAgents(r.items));
  }, [tenantCode]);

  const agentName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of agents) m.set(a.uid, a.displayName || a.email || a.uid);
    return m;
  }, [agents]);

  async function save() {
    if (!draft || !draft.name.trim()) return;
    setSaving(true);
    try {
      const body = { name: draft.name.trim(), agentUids: draft.agentUids, inboxIds: draft.inboxIds };
      if (draft.id) await api.patch('teams', draft.id, body);
      else await api.create('teams', body);
      setDraft(null);
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }
  async function remove(t: Team) {
    if (!window.confirm(`Delete team "${t.name}"?`)) return;
    await api.delete('teams', t.id);
  }

  function toggle(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Teams</h1>
      <p style={{ ...sx.hint, marginTop: 0, marginBottom: 16, fontSize: 13 }}>
        A team groups support agents so chats can be assigned or auto-routed to the group, and so escalations land with
        the right people. Add or remove agents below.
      </p>

      {!draft && canEdit && (
        <button
          style={{ ...sx.btnPrimary, marginBottom: 16 }}
          onClick={() => setDraft({ name: '', agentUids: [], inboxIds: [] })}
        >
          + New team
        </button>
      )}

      {draft && (
        <div style={sx.card}>
          <label style={sx.label}>Team name</label>
          <input
            style={sx.input}
            value={draft.name}
            placeholder="e.g. Bekesher Support"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>Agents ({draft.agentUids.length} selected)</label>
            {agents.length === 0 ? (
              <p style={sx.hint}>
                No support agents found. Agents appear here once they've signed in (support role). If someone is
                missing, have them open the console once.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8 }}>
                {agents.map((a) => (
                  <label key={a.uid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={draft.agentUids.includes(a.uid)}
                      onChange={() => setDraft({ ...draft, agentUids: toggle(draft.agentUids, a.uid) })}
                    />
                    <span>{a.displayName || a.email || a.uid}</span>
                    {a.email && a.displayName && <span style={{ color: '#9ca3af', fontSize: 12 }}>{a.email}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          {inboxes.length > 1 && (
            <div style={{ marginTop: 14 }}>
              <label style={sx.label}>Inboxes this team covers (optional)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {inboxes.map((ib) => {
                  const on = draft.inboxIds.includes(ib.id);
                  return (
                    <button
                      key={ib.id}
                      type="button"
                      onClick={() => setDraft({ ...draft, inboxIds: toggle(draft.inboxIds, ib.id) })}
                      style={{
                        ...sx.btnSecondary, padding: '4px 10px',
                        background: on ? '#eff6ff' : 'white',
                        borderColor: on ? '#93c5fd' : '#d1d5db',
                        color: on ? '#1d4ed8' : '#374151', fontWeight: on ? 600 : 400,
                      }}
                    >
                      {ib.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ ...sx.row, marginTop: 16, marginBottom: 0 }}>
            <button style={sx.btnPrimary} disabled={!draft.name.trim() || saving} onClick={save}>
              {saving ? 'Saving…' : draft.id ? 'Save team' : 'Create team'}
            </button>
            <button style={sx.btnSecondary} disabled={saving} onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={sx.hint}>Loading…</p>
      ) : teams.length === 0 ? (
        <p style={sx.hint}>No teams yet.</p>
      ) : (
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Team</th>
              <th style={sx.th}>Agents</th>
              <th style={sx.th}>Inboxes</th>
              <th style={sx.th}></th>
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.id}>
                <td style={sx.td}>{t.name}</td>
                <td style={{ ...sx.td, color: '#6b7280', fontSize: 12 }}>
                  {(t.agentUids ?? []).length === 0
                    ? '—'
                    : (t.agentUids ?? []).map((u) => agentName.get(u) ?? u).join(', ')}
                </td>
                <td style={sx.td}>{(t.inboxIds ?? []).length || '—'}</td>
                <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit && (
                    <>
                      <button
                        style={{ ...sx.btnSecondary, marginRight: 6 }}
                        onClick={() => setDraft({ id: t.id, name: t.name, agentUids: t.agentUids ?? [], inboxIds: t.inboxIds ?? [] })}
                      >Edit</button>
                      <button style={sx.btnDanger} onClick={() => remove(t)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
